import { useCallback } from "react";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { getUniqueLocations } from "../utilFunctions";
import {
	computeDayTargetContext,
	computeLocationTotalTarget,
	countCoverageNursesOnLocation,
	locationHasConfiguredTarget,
	shiftNursesForDay,
} from "./locationTarget";

/**
 * Per-location staffing picture for a single day/shift, as read by the location picker's par pills.
 *
 * `current` is how many staff are ALREADY on the location for those hours (no `+1` — this is a status
 * read, not the "would adding one exceed?" projection that `useStaffingTargetCheck` computes). `par` is the
 * location total target; `hasPar` distinguishes a real "0 required" from an unconfigured location so the
 * latter shows a muted "no par" rather than a red gap; `belowPar` is understaffed vs a configured target.
 */
export type LocationParCount = { current: number; par: number; hasPar: boolean; belowPar: boolean };

export type LocationParCountsParams = { weekIndex: number; dayNo: number; shiftIndex: number; isActual: boolean };

export const useLocationParCounts = () => {
	const { hospitalData, chosenDepartment, isHospitalView } = useHospitalData();

	return useCallback(
		({ weekIndex, dayNo, shiftIndex, isActual }: LocationParCountsParams): Record<string, LocationParCount> => {
			const context = computeDayTargetContext(hospitalData, chosenDepartment, weekIndex, dayNo, isHospitalView);
			const day = hospitalData.weeks[weekIndex]?.weeklyShifts[shiftIndex]?.days[dayNo];
			const shiftNurses = shiftNursesForDay(day, isActual);

			const result: Record<string, LocationParCount> = {};
			for (const name of getUniqueLocations(chosenDepartment.locations, false).map((loc) => loc.location)) {
				const matchingLocations = chosenDepartment.locations.filter((loc) => loc.location === name);
				const hasPar = matchingLocations.some(locationHasConfiguredTarget);
				const par = context ? computeLocationTotalTarget(matchingLocations, context, chosenDepartment.statsMode) : 0;
				const current = countCoverageNursesOnLocation(shiftNurses, name);
				result[name] = { current, par, hasPar, belowPar: hasPar && current < par };
			}
			return result;
		},
		[hospitalData, chosenDepartment, isHospitalView],
	);
};
